import {Component, ElementRef, forwardRef, Host, OnInit, Optional, SkipSelf, ViewChild} from '@angular/core';
import {ControlContainer, NG_VALUE_ACCESSOR} from '@angular/forms';
import {MatAutocomplete, MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';

import {BehaviorSubject} from 'rxjs';

import {BaseComponent, PhoneAutocompleteControl} from '../../classes';

import {PhoneCodeOptionInterface} from './interfaces/phone-code-option.interface';
import {PhoneAutocompleteValueInterface} from './interfaces/phone-autocomplete-value.interface';
import {PhoneAutocompleteCountryInterface} from './interfaces/phone-autocomplete-country.interface';

@Component({
    selector: 'app-phone-autocomplete',
    templateUrl: './phone-autocomplete.component.html',
    styleUrls: ['./phone-autocomplete.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => PhoneAutocompleteComponent),
        multi: true
    }]
})
export class PhoneAutocompleteComponent extends BaseComponent<PhoneAutocompleteValueInterface,
    PhoneAutocompleteValueInterface, PhoneAutocompleteControl> implements OnInit {

    private readonly phoneCodeMaxLength: number;

    private countries: PhoneAutocompleteCountryInterface[];
    private codes: PhoneCodeOptionInterface[];

    public filteredCodes: BehaviorSubject<PhoneCodeOptionInterface[]>;

    @ViewChild('autocompleteInput', {static: true}) autocompleteInputReference: ElementRef;
    @ViewChild('autocomplete', {static: true}) autocompleteReference: MatAutocomplete;
    @ViewChild('input', {static: true}) inputReference: ElementRef;

    constructor(
        @Optional() @Host() @SkipSelf()
        protected controlContainer: ControlContainer
    ) {
        super(controlContainer);

        this.phoneCodeMaxLength = 4;
    }

    private formatCode(value: string): string {
        const initialValue = value;
        const strippedValue = initialValue.replace(/[^0-9\\+]/g, '');

        /**
         * If initial value during input event execution
         * is bigger than the max length, the last entered
         * character is removed from the string.
         */
        if (initialValue.length > this.phoneCodeMaxLength) {
            return initialValue.slice(0, -1);
        }

        /**
         * Strip all characters different than [0-9] and '+'.
         */
        if (initialValue !== strippedValue) {
            return strippedValue;
        }
        /** ******************************** */

        /**
         * Strip all '+' - es in initial value
         * and add leading '+' to it.
         */
        return '+' + initialValue.replace(/[\\+]/g, '');
    }

    private initPhoneCodeOptions(): PhoneCodeOptionInterface[] {
        this.countries = this.asFormsControl().countries;

        return this.countries
            .sort((current: PhoneAutocompleteCountryInterface, next: PhoneAutocompleteCountryInterface) => {
                if (current.name.substr(0, 1) < next.name.substr(0, 1)) {
                    return -1;
                }

                if (current.name.substr(0, 1) > next.name.substr(0, 1)) {
                    return 1;
                }

                return 0;
            })
            .filter((country: PhoneAutocompleteCountryInterface) => {
                return country.phoneCode;
            }).map((country: PhoneAutocompleteCountryInterface) => {
                return {
                    label: country.phoneCode + '(' + country.name + ')',
                    value: country.phoneCode,
                    classes: this.countryIconClassPrefix + country.id.toLowerCase()
                };
            });
    }

    ngOnInit(): void {
        if (!this.value) {
            this.value = {
                code: null,
                phone: ''
            };
        }

        this.codes = this.initPhoneCodeOptions();

        if (this.codes && this.codes.length > 0) {
            this.filteredCodes = new BehaviorSubject(this.codes);
        }

        this.checkInvalid();
    }

    filterCodes(event): void {
        const value = event.target.value;

        this.executeFilter(value);
    }

    filterCodesOnPaste(event: ClipboardEvent): void {
        const value = event.clipboardData.getData('text');

        this.executeFilter(value);
    }

    executeFilter(value: string): boolean {
        let pattern = '';

        value = this.formatCode(value);

        pattern += '\\' + value + '.*';

        const regex = new RegExp(pattern);

        const filteredCodes = this.codes.filter((code: PhoneCodeOptionInterface) => {
            return regex.test(code.label);
        });

        this.filteredCodes.next(filteredCodes);

        /**
         * Set country if in the array have only one suggestion.
         */
        if (filteredCodes.length === 1) {
            this.value.code = filteredCodes[0].value;
        } else {
            this.value.code = null;
        }

        this.changeValue(this.value);

        return true;
    }

    enterCode(event: MatAutocompleteSelectedEvent): void {
        this.autocompleteInputReference.nativeElement.value = event.option.viewValue;
        this.value.code = event.option.value;

        this.changeValue(this.value);
    }

    enterPhone(event): boolean {
        const initialValue = event.target.value;
        const strippedValue = initialValue.replace(/[^0-9]/g, '');

        if (initialValue !== strippedValue) {
            event.target.value = strippedValue;

            return false;
        }

        this.value.phone = initialValue;

        this.changeValue(this.value);

        return true;
    }

    changeValue(value: PhoneAutocompleteValueInterface): void {
        this.onChange(value);
        this.checkInvalid();
        this.control.resetErrors();

        super.writeValue(value);
    }

    writeValue(value: PhoneAutocompleteValueInterface): void {
        if (value) {
            let filteredCodes = [];

            if (this.codes) {
                filteredCodes = this.codes.filter((code: PhoneCodeOptionInterface) => {
                    return value.code === code.value;
                });
            }

            if (filteredCodes.length !== 0) {
                this.autocompleteInputReference.nativeElement.value = filteredCodes[0].label;
                this.inputReference.nativeElement.value = value.phone;

                super.writeValue({
                    code: filteredCodes[0].value,
                    phone: value.phone
                });
            } else {
                this.autocompleteInputReference.nativeElement.value = null;
                this.inputReference.nativeElement.value = '';

                super.writeValue({
                    code: null,
                    phone: ''
                });
            }
        }

        this.checkInvalid();
        this.control.resetErrors();
    }

    asFormsControl(): PhoneAutocompleteControl {
        if (!this.control) {
            this.control = new PhoneAutocompleteControl({
                name: '',
                value: {
                    code: null,
                    phone: ''
                },
                placeholder: '',
                validators: [],
                componentType: 0,
                invalid: false,
                errors: [],
                countries: []
            });
        }

        return (this.control as PhoneAutocompleteControl);
    }

    trackByCode(index: number, item: PhoneCodeOptionInterface): string {
        return item.value;
    }
}
